import ButtonComponent from 'components/button/buttonComponent';
import { ReactComponent as ChevronDownIcon } from 'assets/icons/chevron-down.svg';
import { ReactComponent as ChevronUpIcon } from 'assets/icons/chevron-up-fix.svg';
import React, { useState } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

export interface CardProps {
  title: string;
  subtitle?: string | React.ReactNode;
  children: React.ReactNode;
  backgroundColor?: string;
  alwaysExpanded?: boolean;
  rightComponent?: React.ReactNode;
  hideMobileSubtitle?: boolean;
  showSeparator?: boolean;
  testId?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  rightComponentWrap?: boolean;
  defaultExpanded?: boolean;
  counter?: React.ReactNode;
}

export const Card: React.FC<CardProps> = ({
  title,
  subtitle,
  children,
  backgroundColor = 'bg-white',
  alwaysExpanded = false,
  rightComponent = null,
  rightComponentWrap = true,
  hideMobileSubtitle = false,
  showSeparator = false,
  defaultExpanded = false,
  testId,
  titleClassName = 'flex text-h4 text-charcoal-gray font-semibold',
  subtitleClassName,
  counter,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const ArrowIcon = isExpanded ? ChevronUpIcon : ChevronDownIcon;

  const handleExpandIconClick = () => {
    setIsExpanded((isExpanded) => !isExpanded);
  };

  return (
    <div
      className={`flex cursor-pointer flex-col items-center px-5 pt-5 pb-30 desktop:p-30 desktop:gap-5 gap-2.5 ${backgroundColor} w-full h-full rounded-10`}
      data-testid={testId}
      onClick={handleExpandIconClick}
    >
      <div className="flex flex-col w-full">
        <div className="flex flex-col">
          <div className="flex flex-row w-full items-center justify-between p-0 gap-2.5">
            <div className="flex flex-row justify-between desktop:justify-start items-center gap-4">
              <h3 className={titleClassName}>{title}</h3>
              <div>{counter}</div>
            </div>
            <div className="flex flex-row items-center gap-2">
              <div
                className={twMerge(
                  'desktop:flex flex-row items-center gap-2 ',
                  rightComponentWrap ? 'hidden' : 'flex',
                )}
              >
                {rightComponent}
              </div>
              {!alwaysExpanded && (
                <ButtonComponent
                  testID={'arrow-expand-icon'}
                  type="underline"
                  iconPosition="right"
                  Icon={ArrowIcon}
                  iconWidth="w-[12px]"
                  fill="med-gray-3"
                  stroke="med-gray-3"
                />
              )}
            </div>
          </div>
        </div>

        {(isExpanded || alwaysExpanded) && (
          <div className="hidden desktop:inline-block">{subtitle}</div>
        )}
      </div>
      {!hideMobileSubtitle && (
        <div
          className={twJoin(
            'text-xs self-start desktop:hidden text-med-gray-3',
            subtitleClassName,
          )}
        >
          {subtitle}
        </div>
      )}
      <div
        className={twMerge(
          'desktop:hidden',
          rightComponentWrap ? 'flex' : 'hidden',
        )}
      >
        {rightComponent}
      </div>
      {showSeparator && (isExpanded || alwaysExpanded) && (
        <div className="w-full border-t-2 border-card-border-color" />
      )}
      {(isExpanded || alwaysExpanded) && children}
    </div>
  );
};
